/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  Divider,
  Card,
  CardContent,
  CardActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  FormControlLabel,
  Switch,
  FormHelperText
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InvoiceDetail from './InvoiceDetail';
import RecordPayment from './RecordPayment';
import { 
  calculateChamberCost, 
  calculateResourceCost, 
  calculateTotalBillAmount,
  calculateDays,
  isAssignmentActive,
  clampEndDateToAssignment
} from '../../utils/invoiceCalculations';
import { consolidateChamberUsage } from '../../utils/chamberUsageConsolidate';
import { priceChamberRow, formatRate, formatBilledUnits } from '../../utils/ratePricing';
import PhoneInput from '../shared/PhoneInput';
import { formatPhoneNumber, displayPhoneNumber, formatAccountNumber, displayAccountNumber } from '../../utils/formatUtils';

// Default bill_to company when the user has no company set on their profile:
// NCSU users default to 'NCSU', everyone else is treated as a commercial client.
const getCompanyFallback = (isNcsu) => (isNcsu ? 'NCSU' : 'Commercial Client');

// Helper function to format date as YYYY-MM-DD in LOCAL timezone (not UTC)
// This prevents the "off by one day" issue when using toISOString()
const formatLocalDate = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Display a calendar-date field (e.g. billing period) using UTC parts so the
// shown day matches exactly what was stored, with no timezone shift.
const fmtUTCDate = (value) => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'N/A';
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
};

// Helper function to parse a date from database as a local date (avoiding timezone shift)
// When a date is stored as "2025-01-01T00:00:00.000Z", this ensures we get Jan 1 locally
const parseAsLocalDate = (dateValue) => {
  if (!dateValue) return null;
  if (dateValue instanceof Date) {
    // If already a Date, extract UTC parts and create local date
    return new Date(dateValue.getUTCFullYear(), dateValue.getUTCMonth(), dateValue.getUTCDate(), 12, 0, 0, 0);
  }
  // If string, parse and create local date from UTC parts
  const d = new Date(dateValue);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12, 0, 0, 0);
};

// Helper function to extract invoice number from invoice_id
// e.g., "202601270728_INV011" -> 11
const getInvoiceNumber = (invoiceId) => {
  if (!invoiceId) return 'N/A';
  const parts = invoiceId.split('_INV');
  if (parts.length < 2) return 'N/A';
  const num = parseInt(parts[1], 10);
  return isNaN(num) ? 'N/A' : num;
};

const formatRateBasis = (basis) => {
  if (!basis) return '/ day';
  return `/ ${basis}`;
};

const GenerateNewInvoice = ({ open, onClose, selectedProject, onInvoiceCreated, setSelectedProject, resourceOnly = false }) => {
  // State for billing period
  const [billStartDate, setBillStartDate] = useState('');
  const [billEndDate, setBillEndDate] = useState('');
  
  // State for adjustments
  const [adjustments, setAdjustments] = useState([]);
  
  // State for Bill To information
  const [billTo, setBillTo] = useState({
    name: '',
    company: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: ''
  });
  const [selectedBillToUser, setSelectedBillToUser] = useState('');
  const [loadingBillToUser, setLoadingBillToUser] = useState(false);
  
  // State for invoice comment
  const [invoiceComment, setInvoiceComment] = useState('');
  
  // State for distribution type
  const [distributionType, setDistributionType] = useState('user_based'); // 'user_based' or 'account_based'
  
  // State for user distributions
  const [userDistributions, setUserDistributions] = useState([]);
  
  // State for account distributions
  const [accountDistributions, setAccountDistributions] = useState([]);
  
  // State for previous balances
  const [previousBalances, setPreviousBalances] = useState([]);
  const [loadingPreviousBalances, setLoadingPreviousBalances] = useState(false);
  
  // State for unbilled periods data
  const [unbilledPeriodsData, setUnbilledPeriodsData] = useState(null);
  const [loadingUnbilledPeriods, setLoadingUnbilledPeriods] = useState(false);
  
  // State for chamber and resource usage
  const [chamberUsage, setChamberUsage] = useState([]);
  const [resourceUsage, setResourceUsage] = useState([]);
  
  // State for editable chamber usage dates
  const [editableChamberUsage, setEditableChamberUsage] = useState({});
  const [chamberDateOverrides, setChamberDateOverrides] = useState({});
  
  // State for excluded chamber usage rows (rows user wants to skip in this invoice)
  const [excludedChamberRows, setExcludedChamberRows] = useState([]);
  // Include resource usage in this invoice (when false, resources stay unbilled for next invoice)
  const [includeResourceUsage, setIncludeResourceUsage] = useState(true);
  
  // Post-invoice creation state
  const [createdInvoice, setCreatedInvoice] = useState(null);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(false);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  
  // Loading state
  const [loading, setLoading] = useState(false);
  
  // Add user states
  const [allUsers, setAllUsers] = useState([]);
  const [originalProjectUsers, setOriginalProjectUsers] = useState([]); // Track original project users
  const [newlyAddedUsers, setNewlyAddedUsers] = useState([]); // Track users added during invoice generation
  
  // Add account states
  const [selectedUserForNewAccount, setSelectedUserForNewAccount] = useState('');
  const [selectedUserAccounts, setSelectedUserAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [newAccountNumber, setNewAccountNumber] = useState('');
  const [newSourceOfFunds, setNewSourceOfFunds] = useState('');
  const [isNewAccount, setIsNewAccount] = useState(false);
  const [loadingUserAccounts, setLoadingUserAccounts] = useState(false);
  
  // Account mapping state for Accounts Receivable
  const [accountMapping, setAccountMapping] = useState({
    facility_id: '',
    account_number: ''
  });
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityAccounts, setSelectedFacilityAccounts] = useState([]);
  
  // Add a new state for the dropdown mode
  const [displayMode, setDisplayMode] = useState('form'); // 'form' or 'summary'
  
  // State for users
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // State for invoice detail dialog
  const [invoiceDetailOpen, setInvoiceDetailOpen] = useState(false);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  
  // Fetch all users for the add user dialog
  const fetchAllUsers = async () => {
    try {
      const response = await axios.get('/api/users/');
      setAllUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  // Fetch facilities for account mapping
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const response = await axios.get('/api/facilities');
        setFacilities(response.data);
      } catch (error) {
        console.error('Error fetching facilities:', error);
      }
    };
    fetchFacilities();
  }, []);

  // Handle facility selection for account mapping
  const handleFacilityChange = (facilityId) => {
    const facility = facilities.find(f => f._id === facilityId);
    setAccountMapping({
      facility_id: facilityId,
      account_number: ''
    });
    setSelectedFacilityAccounts(facility?.accounts_receivable || []);
  };

  // Handle account selection for account mapping
  const handleAccountMappingChange = (accountNumber) => {
    setAccountMapping({
      ...accountMapping,
      account_number: accountNumber
    });
  };
  
  // Add user to project distributions
  const handleAddUserToProject = async (userId) => {
    const user = allUsers.find(u => u._id === userId);
    if (!user) return;
    
    try {
      // Add user to the actual project
      const response = await axios.post(`/api/admin/projects/${selectedProject._id}/users`, {
        user_id: userId,
        role: 'Member'
      });
      
      // Update the selected project with the new user
      const updatedProject = response.data;
      setSelectedProject(updatedProject);
      
      // Extract role string from ProjectRole object if populated
      const roleString = typeof user.role === 'object' && user.role !== null
        ? (user.role.role || 'Member')
        : (user.role || 'Member');
      
      // If this is the first user, give them 100%
      if (userDistributions.length === 0) {
        const newDistribution = {
          user_id: userId, // Use string ID
          user_name: `${user.first_name} ${user.last_name}`,
          email: user.email,
          department: user.department?.name || 'N/A',
          role: roleString,
          percent_share: '100.00'
        };
        
        setUserDistributions([newDistribution]);
        return;
      }
      
      // For additional users, they get 0% by default
      const newDistribution = {
        user_id: userId, // Use string ID
        user_name: `${user.first_name} ${user.last_name}`,
        email: user.email,
        department: user.department?.name || 'N/A',
        role: roleString,
        percent_share: '0.00'
      };
      
      setUserDistributions([...userDistributions, newDistribution]);
      setNewlyAddedUsers([...newlyAddedUsers, userId]); // Track this user as newly added
      toast.success(`${user.first_name} ${user.last_name} added to project`);
    } catch (error) {
      console.error('Error adding user to project:', error);
      toast.error('Failed to add user to project');
    }
  };
  
  // Remove newly added user from distributions and project
  const handleRemoveNewlyAddedUser = async (userId) => {
    try {
      // Remove user from the actual project in database
      const response = await axios.delete(`/api/admin/projects/${selectedProject._id}/users/${userId}`);
      
      // Update the selected project with the response
      setSelectedProject(response.data);
      
      // Remove from user distributions
      const updatedDistributions = userDistributions.filter(dist => {
        const distUserId = typeof dist.user_id === 'string' 
          ? dist.user_id 
          : dist.user_id?._id;
        return distUserId !== userId;
      });
      setUserDistributions(updatedDistributions);
      
      // Remove from newly added users list
      setNewlyAddedUsers(newlyAddedUsers.filter(id => id !== userId));
      
      toast.success('User removed from project and distribution');
    } catch (error) {
      console.error('Error removing user from project:', error);
      toast.error('Failed to remove user from project');
    }
  };
  
  // Fetch unpaid invoices for the project
  const fetchUnpaidInvoices = useCallback(async () => {
    if (!selectedProject?._id) return;
    
    try {
      setLoadingPreviousBalances(true);
      const response = await axios.get(`/api/invoices/unbilled/${selectedProject._id}`);
      
      
      // Get unpaid invoices and format them for previous balances
      if (response.data && response.data.unpaidInvoices && Array.isArray(response.data.unpaidInvoices) && response.data.unpaidInvoices.length > 0) {
        const balances = response.data.unpaidInvoices.map(invoice => ({
          invoice_id: invoice.invoice_id,
          // Use current_period_amount (invoice's own charges) instead of total_amount to avoid double-counting
          original_amount: invoice.current_period_amount || invoice.total_amount || 0,
          amount_paid: invoice.amount_paid || 0,
          // Use own_remaining_balance (remaining from this invoice's own charges only)
          remaining_amount: invoice.own_remaining_balance || invoice.remaining_balance || 0,
          generation_date_formatted: invoice.generation_date_formatted || 'N/A',
          billing_period_formatted: invoice.billing_period_formatted || 'N/A',
          status: invoice.status || 'Unknown',
          included: false // By default exclude from new invoice (admin can tick to include)
        }));
        
        setPreviousBalances(balances);
      } else {
        setPreviousBalances([]);
      }
    } catch (error) {
      console.error('Error fetching unpaid invoices:', error);
      setPreviousBalances([]);
    } finally {
      setLoadingPreviousBalances(false);
    }
  }, [selectedProject?._id]);

  // Fetch unbilled periods for chamber assignments
  const fetchUnbilledPeriods = useCallback(async () => {
    if (!selectedProject?._id) return;
    
    try {
      setLoadingUnbilledPeriods(true);
      const response = await axios.get(`/api/invoices/unbilled-periods/${selectedProject._id}`);
      setUnbilledPeriodsData(response.data);
    } catch (error) {
      console.error('Error fetching unbilled periods:', error);
      setUnbilledPeriodsData(null);
    } finally {
      setLoadingUnbilledPeriods(false);
    }
  }, [selectedProject?._id]);

  // Fetch existing invoices
  const fetchExistingInvoices = useCallback(async () => {
    if (!selectedProject?._id) return;
    
    try {
      // The correct API endpoint is /invoices/by-project/
      const response = await axios.get(`/api/invoices/by-project/${selectedProject._id}`);
      
      // If there are existing invoices, we can use this information for reference
      // but we should NOT automatically switch to summary mode - 
      // always show the form directly when opening the dialog to create a new invoice
      if (response.data && response.data.length > 0) {
        // Existing invoices found, but don't change display mode
        // The form will show up directly for creating a new invoice
      }
    } catch (error) {
      // Don't show error message for this specific error since it's expected when project changes
      if (!error.message?.includes('assigned_chambers')) {
        toast.error('Failed to fetch existing invoices: ' + (error.response?.data?.message || error.message));
      }
    }
  }, [selectedProject?._id, createdInvoice, setDisplayMode, toast]);

  // Also add a useEffect to handle migrating data from old schema to new schema, if needed
  useEffect(() => {
    // If the project has "assigned_chambers" instead of "chamber_assignments", migrate the data
    if (selectedProject && selectedProject.assigned_chambers && !selectedProject.chamber_assignments) {
      // Create temporary chamber_assignments from assigned_chambers for UI to use
      const tempChamberAssignments = (selectedProject.assigned_chambers || []).map(chamber => {
        const units = (chamber.assignments || []).map(assignment => ({
          unit: assignment.unit,
          quantity: assignment.current_quantity || 0,
          start_date: assignment.start_date,
          end_date: assignment.end_date,
          status: assignment.status || 'Active'
        })) || [];
        
        return {
          chamber: chamber.chamber,
          units
        };
      });
      
      // Add this temporary data to the selectedProject but don't modify original
      selectedProject.chamber_assignments = tempChamberAssignments;
    }
    
    // Similarly for resources
    if (selectedProject && selectedProject.assigned_resources && !selectedProject.resource_assignments) {
      // Create temporary resource_assignments from assigned_resources for UI to use
      const tempResourceAssignments = (selectedProject.assigned_resources || []).map(resource => ({
        resource: resource.resource,
        quantity: resource.quantity || 0
      }));
      
      // Add this temporary data to the selectedProject but don't modify original
      selectedProject.resource_assignments = tempResourceAssignments;
    }
  }, [selectedProject]);

  // Handle Bill To user selection
  const handleBillToUserSelect = async (userEmail) => {
    setSelectedBillToUser(userEmail);
    
    if (!userEmail) {
      setBillTo({
        name: '',
        company: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        email: ''
      });
      return;
    }
    
    const projectUser = selectedProject?.users?.find(u => u.email === userEmail);
    if (projectUser) {
      const fullName = `${projectUser.first_name || ''} ${projectUser.last_name || ''}`.trim();
      
      setLoadingBillToUser(true);
      try {
        const response = await axios.get(`/api/users/profile/by-email/${encodeURIComponent(userEmail)}`);
        const userData = response.data;
        
        setBillTo({
          name: fullName,
          company: userData.company || getCompanyFallback(userData.is_ncsu),
          address_line1: userData.address_line1 || userData.address || '',
          address_line2: userData.address_line2 || '',
          city: userData.city || '',
          state: userData.state || '',
          zip: userData.zip || userData.zip_code || '',
          phone: userData.contact_number || userData.phone || '',
          email: userEmail
        });
      } catch (error) {
        setBillTo({
          name: fullName,
          company: getCompanyFallback(projectUser.is_ncsu),
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          zip: '',
          phone: '',
          email: userEmail
        });
      } finally {
        setLoadingBillToUser(false);
      }
    }
  };

  // Handle adjustments
  const handleAddAdjustment = () => {
    setAdjustments([...adjustments, { amount: '', comments: '' }]);
  };
  
  const handleRemoveAdjustment = (index) => {
    const newAdjustments = [...adjustments];
    newAdjustments.splice(index, 1);
    setAdjustments(newAdjustments);
  };
  
  const handleAdjustmentChange = (index, field, value) => {
    const newAdjustments = [...adjustments];
    newAdjustments[index][field] = value;
    setAdjustments(newAdjustments);
  };
  
  // Handle user distribution changes with validation
  const handleUserDistributionChange = (userId, value) => {
    // Convert value to number and ensure it's between 0 and 100
    const numValue = parseFloat(value) || 0;
    if (numValue < 0 || numValue > 100) {
      toast.error('Percentage must be between 0 and 100');
      return;
    }
    
    // Calculate new total with this change
    const newTotal = userDistributions.reduce((sum, dist) => {
      // Convert user_id to string ID if it's an object
      const distUserId = typeof dist.user_id === 'string' 
        ? dist.user_id 
        : dist.user_id?._id;
        
      if (distUserId === userId) {
        return sum + numValue;
      }
      return sum + (parseFloat(dist.percent_share) || 0);
    }, 0);
    
    // Check if new total would exceed 100%
    if (newTotal > 100) {
      toast.error('Total distribution cannot exceed 100%');
      return;
    }
    
    const newDistributions = userDistributions.map(dist => {
      // Convert user_id to string ID if it's an object
      const distUserId = typeof dist.user_id === 'string' 
        ? dist.user_id 
        : dist.user_id?._id;
        
      if (distUserId === userId) {
        return { ...dist, percent_share: value };
      }
      return dist;
    });
    
    setUserDistributions(newDistributions);
  };

  // Handle account distribution changes with validation (use index for duplicate account numbers)
  const handleAccountDistributionChange = (index, value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < 0 || numValue > 100) {
      toast.error('Percentage must be between 0 and 100');
      return;
    }
    
    const newTotal = accountDistributions.reduce((sum, dist, i) => 
      sum + (i === index ? numValue : (parseFloat(dist.percent_share) || 0)), 0);
    if (newTotal > 100) {
      toast.error('Total distribution cannot exceed 100%');
      return;
    }
    
    const newDistributions = accountDistributions.map((dist, i) => 
      i === index ? { ...dist, percent_share: value } : dist
    );
    
    setAccountDistributions(newDistributions);
  };

  // Add account to distribution (include user_id for correct ledger mapping)
  const handleAddAccountToDistribution = (accountNumber, sourceOfFunds, userId, userEmail) => {
    // Check if same (user, account) already exists
    const exists = accountDistributions.some(dist => 
      dist.account_number === accountNumber && (dist.user_id?.toString() === userId?.toString() || dist.user_email === userEmail)
    );
    if (exists) {
      toast.error('Account already added to distribution');
      return;
    }

    const newDistribution = {
      account_number: accountNumber,
      source_of_funds: sourceOfFunds,
      user_id: userId,
      user_email: userEmail,
      percent_share: accountDistributions.length === 0 ? '100.00' : '0.00'
    };
    setAccountDistributions([...accountDistributions, newDistribution]);
  };

  // Remove account from distribution (by index to handle duplicate account numbers)
  const handleRemoveAccountFromDistribution = (index) => {
    const newDistributions = accountDistributions.filter((_, i) => i !== index);
    setAccountDistributions(newDistributions);
  };

  // Remove account from project AND distribution (admin can undo adding during invoice)
  const handleRemoveAccountFromProjectAndDistribution = async (index) => {
    const dist = accountDistributions[index];
    if (!dist || !selectedProject?._id) return;
    const userEmail = dist.user_email;
    if (!userEmail) {
      toast.error('Cannot remove: missing user info');
      return;
    }
    try {
      const response = await axios.delete(`/api/admin/projects/${selectedProject._id}/funding`, {
        data: {
          account_number: dist.account_number,
          user_email: userEmail,
          user_id: dist.user_id
        }
      });
      if (response.data) setSelectedProject(response.data);
      handleRemoveAccountFromDistribution(index);
      toast.success('Account removed from project');
    } catch (error) {
      console.error('Error removing account from project:', error);
      toast.error(error.response?.data?.message || 'Failed to remove account from project');
    }
  };

  // Handle user selection for new account
  const handleUserSelectionForNewAccount = async (email) => {
    setSelectedUserForNewAccount(email);
    setSelectedAccountId('');
    setNewAccountNumber('');
    setNewSourceOfFunds('');
    setIsNewAccount(false);
    
    if (!email) {
      setSelectedUserAccounts([]);
      return;
    }
    
    try {
      setLoadingUserAccounts(true);
      const encodedEmail = encodeURIComponent(email);
      const response = await axios.get(`/api/users/with-accounts/${encodedEmail}`);
      setSelectedUserAccounts(response.data.account_numbers || []);
    } catch (error) {
      console.error('Error fetching user accounts:', error);
      setSelectedUserAccounts([]);
    } finally {
      setLoadingUserAccounts(false);
    }
  };

  // Handle account selection for adding to distribution
  const handleAccountSelection = (accountId) => {
    setSelectedAccountId(accountId);
    
    if (accountId === 'new') {
      setIsNewAccount(true);
      setNewAccountNumber('');
      setNewSourceOfFunds('');
    } else if (accountId) {
      const selectedAccount = selectedUserAccounts.find(acc => acc._id === accountId);
      if (selectedAccount) {
        setIsNewAccount(false);
        setNewAccountNumber(selectedAccount.number);
        setNewSourceOfFunds(selectedAccount.source);
      }
    } else {
      setIsNewAccount(false);
      setNewAccountNumber('');
      setNewSourceOfFunds('');
    }
  };

  // Add account to project and distribution
  const handleAddAccountToProject = async () => {
    if (!selectedUserForNewAccount || !newAccountNumber || !newSourceOfFunds) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // If it's a new account, save it to the user first
      if (isNewAccount || selectedAccountId === 'new') {
        const userResponse = await axios.get(`/api/users/with-accounts/${encodeURIComponent(selectedUserForNewAccount)}`);
        const userId = userResponse.data._id;
        
        await axios.post(`/api/users/${userId}/account-numbers`, {
          number: newAccountNumber,
          source: newSourceOfFunds
        });
        
        toast.success('New account saved to user profile');
      }

      // Add account to project funding
      const projectResponse = await axios.post(`/api/admin/projects/${selectedProject._id}/funding`, {
        account_number: newAccountNumber,
        source_of_funds: newSourceOfFunds,
        user_email: selectedUserForNewAccount,
        is_new_account: isNewAccount || selectedAccountId === 'new'
      });

      // Update the selected project
      setSelectedProject(projectResponse.data);

      // Add to distribution with user_id (from updated project funding or project users)
      const newFundingEntry = projectResponse.data.funding?.find(f => 
        f.account_number === newAccountNumber && f.user_email === selectedUserForNewAccount
      );
      const projectUser = selectedProject.users?.find(u => u.email === selectedUserForNewAccount);
      const userId = newFundingEntry?.user_id || projectUser?.user?._id || projectUser?.user;
      const exists = accountDistributions.some(dist => 
        dist.account_number === newAccountNumber && (dist.user_id?.toString() === userId?.toString() || dist.user_email === selectedUserForNewAccount)
      );
      if (!exists) {
        const newDistribution = {
          account_number: newAccountNumber,
          source_of_funds: newSourceOfFunds,
          user_id: userId,
          user_email: selectedUserForNewAccount,
          percent_share: accountDistributions.length === 0 ? '100.00' : '0.00'
        };
        setAccountDistributions([...accountDistributions, newDistribution]);
      }

      // Reset form
      setSelectedUserForNewAccount('');
      setSelectedAccountId('');
      setNewAccountNumber('');
      setNewSourceOfFunds('');
      setIsNewAccount(false);
      setSelectedUserAccounts([]);
      
      toast.success('Account added to project');
    } catch (error) {
      console.error('Error adding account:', error);
      toast.error('Failed to add account to project');
    }
  };

  // Handle previous balance inclusion toggle
  const handlePreviousBalanceToggle = (invoiceId, included) => {
    const updatedBalances = previousBalances.map(balance => 
      balance.invoice_id === invoiceId 
        ? { ...balance, included }
        : balance
    );
    setPreviousBalances(updatedBalances);
  };

  // Calculate included previous balance total
  const getIncludedPreviousBalanceTotal = () => {
    return previousBalances
      .filter(balance => balance.included)
      .reduce((sum, balance) => sum + (balance.remaining_amount || 0), 0);
  };

  // Initialize bill dates and distributions when project is selected
  useEffect(() => {
    if (!selectedProject || !selectedProject._id) {
      return;
    }
    
    // Calculate proper billing period using unbilled periods data
    const initializeBillingPeriod = async () => {
        try {
          // Fetch unbilled periods data from the backend
          const response = await axios.get(`/api/invoices/unbilled-periods/${selectedProject._id}`);
          const unbilledData = response.data;
          
          let billStart = null;
          let billEnd = null;
          
          if (unbilledData && unbilledData.unbilled_periods && unbilledData.unbilled_periods.length > 0) {
            // Find the earliest start date and latest end date from all unbilled periods
            let earliestStart = null;
            let latestEnd = null;
            
            unbilledData.unbilled_periods.forEach(item => {
              if (item.unbilled_periods && Array.isArray(item.unbilled_periods)) {
                item.unbilled_periods.forEach(period => {
                  // Use parseAsLocalDate to avoid timezone shift from UTC dates
                  const startDate = parseAsLocalDate(period.start_date);
                  const endDate = parseAsLocalDate(period.end_date);
                  
                  if (!earliestStart || startDate < earliestStart) {
                    earliestStart = startDate;
                  }
                  if (!latestEnd || endDate > latestEnd) {
                    latestEnd = endDate;
                  }
                });
              }
            });
            
            billStart = earliestStart;
            
            // Cap the end date to today's date (don't bill for future periods)
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            billEnd = latestEnd && latestEnd > today ? today : latestEnd;
            
            // If billEnd is the far future date (2100-01-01), use today instead
            if (billEnd && billEnd.getFullYear() >= 2100) {
              billEnd = today;
            }
            
          } else {
            // No unbilled periods found - this means everything is already billed
            
            // Set dates to current date range as fallback
            billStart = new Date();
            billEnd = new Date();
            billEnd.setDate(billEnd.getDate() + 30); // 30 days from now as default
          }
          
          // Set the calculated dates (only if they haven't been manually changed)
          if (!billStartDate && billStart) {
            setBillStartDate(formatLocalDate(billStart));
          }
          if (!billEndDate && billEnd) {
            setBillEndDate(formatLocalDate(billEnd));
          }
          
        } catch (error) {
          console.error('Error fetching unbilled periods for date calculation:', error);
          
          // Fallback to current date if API call fails
          const fallbackStart = new Date();
          const fallbackEnd = new Date();
          fallbackEnd.setDate(fallbackEnd.getDate() + 30);
          
          if (!billStartDate) {
            setBillStartDate(formatLocalDate(fallbackStart));
          }
          if (!billEndDate) {
            setBillEndDate(formatLocalDate(fallbackEnd));
          }
        }
    };
    
    // Call the initialization function
    initializeBillingPeriod();
    
    // Set user distributions based on project users
    if (selectedProject && selectedProject.users && Array.isArray(selectedProject.users) && selectedProject.users.length > 0) {
      // Store original project users (only once when project is first loaded)
      if (originalProjectUsers.length === 0) {
        const originalUserIds = selectedProject.users.map(user => 
          typeof user.user === 'string' ? user.user : (user.user?._id || user._id)
        );
        setOriginalProjectUsers(originalUserIds);
      }
      
      // Create distributions for all users
      const newUserDistributions = selectedProject.users.map((user, index) => {
        // Handle both populated and unpopulated user objects
        const userId = typeof user.user === 'string' 
          ? user.user 
          : (user.user?._id || user._id || `user-${index}`);
        
        // Extract role string from ProjectRole object if populated
        const roleString = typeof user.role === 'object' && user.role !== null
          ? (user.role.role || 'Member')
          : (user.role || 'Member');
        
        return {
          user_id: userId,
          user_name: `${user.first_name} ${user.last_name}`,
          email: user.email,
          department: user.department?.name || 'N/A',
          role: roleString,
          // Set 100% for first user, 0% for all others
          percent_share: index === 0 ? '100.00' : '0.00'
        };
      });
      
      setUserDistributions(newUserDistributions);
    } else {
      setUserDistributions([]);
      if (originalProjectUsers.length === 0) {
        setOriginalProjectUsers([]);
      }
    }

    // Set account distributions based on project funding (include user_id for correct ledger mapping)
    if (selectedProject.funding && selectedProject.funding.length > 0) {
      const newAccountDistributions = selectedProject.funding.map((funding, index) => {
        const projectUser = funding.user_email 
          ? selectedProject.users?.find(u => u.email === funding.user_email)
          : (funding.user_id ? selectedProject.users?.find(u => (u.user?._id || u.user)?.toString() === funding.user_id?.toString()) : null);
        const user_id = funding.user_id || projectUser?.user?._id || projectUser?.user;
        return {
          account_number: funding.account_number,
          source_of_funds: funding.source_of_funds,
          user_id: user_id,
          user_email: funding.user_email || projectUser?.email,
          percent_share: index === 0 ? '100.00' : '0.00'
        };
      });
      setAccountDistributions(newAccountDistributions);
    } else {
      setAccountDistributions([]);
    }
    
    // Initialize Bill To from PI (first user with PI role, or first user if no PI role found)
    // Only initialize if no user is already selected (to preserve selection when project is updated)
    if (selectedProject.users && selectedProject.users.length > 0 && !selectedBillToUser) {
      // Try to find PI first
      let piUser = selectedProject.users.find(u => {
        const roleString = typeof u.role === 'object' && u.role !== null
          ? (u.role.role || '')
          : (u.role || '');
        return roleString === 'PI' && !u.isDeleted;
      });
      
      // Fallback to first active user if no PI found
      if (!piUser) {
        piUser = selectedProject.users.find(u => !u.isDeleted) || selectedProject.users[0];
      }
      
      if (piUser) {
        // Set the selected user and basic bill_to info
        setSelectedBillToUser(piUser.email);
        const fullName = `${piUser.first_name || ''} ${piUser.last_name || ''}`.trim();
        
        setBillTo({
          name: fullName,
          company: getCompanyFallback(piUser.is_ncsu),
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          zip: '',
          phone: '',
          email: piUser.email
        });
        
        axios.get(`/api/users/profile/by-email/${encodeURIComponent(piUser.email)}`)
          .then(response => {
            const userData = response.data;
            setBillTo(prev => ({
              ...prev,
              company: userData.company || getCompanyFallback(userData.is_ncsu),
              address_line1: userData.address_line1 || userData.address || prev.address_line1,
              address_line2: userData.address_line2 || prev.address_line2,
              city: userData.city || prev.city,
              state: userData.state || prev.state,
              zip: userData.zip || userData.zip_code || prev.zip,
              phone: userData.contact_number || userData.phone || prev.phone
            }));
          })
          .catch(err => {
            console.error('Failed to load bill-to details for the project:', err);
          });
      }
    }
    
    // Fetch unpaid invoices for the project
    fetchUnpaidInvoices();
    
    // Fetch unbilled periods for accurate chamber cost calculation
    fetchUnbilledPeriods();
    
    // Also fetch existing invoices to check if any already exist
    fetchExistingInvoices();
  }, [selectedProject, fetchUnpaidInvoices, fetchUnbilledPeriods, fetchExistingInvoices]);

  // Refetch unbilled periods when billing dates change
  useEffect(() => {
    if (selectedProject?._id && billStartDate && billEndDate) {
      fetchUnbilledPeriods();
    }
  }, [billStartDate, billEndDate, selectedProject?._id, fetchUnbilledPeriods]);

  // Calculate total bill amount
  const calculateTotalBillAmount = () => {
    const chamberTotal = resourceOnly ? 0 : (calculateChamberCostWithOverrides().totalCost || 0);
    const resourceTotal = includeResourceUsage ? (calculateResourceCost(selectedProject, billEndDate).totalCost || 0) : 0;
    const adjustmentTotal = adjustments.reduce((sum, adj) => {
      return sum + (parseFloat(adj.amount) || 0);
    }, 0);
    const previousBalanceTotal = getIncludedPreviousBalanceTotal();
    
    return chamberTotal + resourceTotal + adjustmentTotal + previousBalanceTotal;
  };

  // Handle post-invoice actions
  const handleViewPDF = async () => {
    try {
      if (!createdInvoice || !createdInvoice._id || !createdInvoice.invoice_id) {
        toast.error('Invoice information is incomplete');
        return;
      }
      
      // Use the correct API endpoint for PDF
      const response = await axios.get(`/api/invoices/${createdInvoice._id}/pdf?invoice_id=${createdInvoice.invoice_id}`, {
        responseType: 'blob'
      });
      
      // Create a blob URL for the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Open in new tab
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing PDF:', error);
      toast.error('Failed to view PDF: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDownloadPDF = async () => {
    try {
      if (!createdInvoice || !createdInvoice._id || !createdInvoice.invoice_id) {
        toast.error('Invoice information is incomplete');
        return;
      }
      
      // Use the correct API endpoint for PDF
      const response = await axios.get(`/api/invoices/${createdInvoice._id}/pdf?invoice_id=${createdInvoice.invoice_id}`, {
        responseType: 'blob'
      });
      
      // Extract filename from Content-Disposition header if available
      let filename = `invoice-${getInvoiceNumber(createdInvoice.invoice_id)}.pdf`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=(?:(?:"([^"]*)")|([^;\n]*))/i);
        if (filenameMatch && (filenameMatch[1] || filenameMatch[2])) {
          filename = filenameMatch[1] || filenameMatch[2];
        }
      }
      
      // Create a blob URL for the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleShowInvoiceDetail = () => {
    setShowInvoiceDetail(true);
  };

  const handleShowRecordPayment = () => {
    setShowRecordPayment(true);
  };

  const handlePaymentRecorded = (updatedInvoice) => {
    setCreatedInvoice(updatedInvoice);
    setShowRecordPayment(false);
    toast.success('Payment recorded successfully');
  };

  const handleCreateNewInvoice = () => {
    // Switch back to form mode instead of resetting created invoice
    setDisplayMode('form');
    // Don't reset dates to let user create another invoice for a different period
  };

  // Handle chamber usage date changes
  const handleChamberDateChange = (uniqueKey, field, value) => {
    let localDate = null;
    if (value) {
      // Parse date manually to avoid timezone issues
      const [year, month, day] = value.split('-').map(Number);
      localDate = new Date(year, month - 1, day, 12, 0, 0, 0); // month is 0-indexed, set to noon
    }
    
    // Update the date override
    const newOverrides = {
      ...chamberDateOverrides,
      [uniqueKey]: {
        ...chamberDateOverrides[uniqueKey],
        [field]: localDate
      }
    };
    setChamberDateOverrides(newOverrides);
    
    // Update the bill period dates based on chamber usage
    if (field === 'endDate') {
      updateBillEndDateFromChamberUsage(newOverrides);
    } else if (field === 'startDate') {
      updateBillStartDateFromChamberUsage(newOverrides);
    }
  };
  
  // Helper function to update bill start date based on chamber usage
  const updateBillStartDateFromChamberUsage = (overrides = chamberDateOverrides) => {
    const baseCosts = calculateChamberCost(selectedProject, billStartDate, billEndDate, unbilledPeriodsData);
    
    if (!baseCosts || !baseCosts.unitCosts || !Array.isArray(baseCosts.unitCosts)) {
      return;
    }
    
    // Find the earliest start date among all non-excluded chamber usage rows
    let earliestStartDate = null;
    
    baseCosts.unitCosts
      .filter(unitCost => unitCost && !excludedChamberRows.includes(unitCost.uniqueKey))
      .forEach(unitCost => {
        const override = overrides[unitCost.uniqueKey];
        const startDate = override?.startDate || unitCost.startDate;
        
        if (startDate) {
          const dateObj = new Date(startDate);
          if (!earliestStartDate || dateObj < earliestStartDate) {
            earliestStartDate = dateObj;
          }
        }
      });
    
    // Update bill start date if we found a valid earliest date
    if (earliestStartDate) {
      setBillStartDate(formatLocalDate(earliestStartDate));
    }
  };
  
  // Helper function to update bill end date based on chamber usage
  const updateBillEndDateFromChamberUsage = (overrides = chamberDateOverrides) => {
    // Get the base chamber costs to access all unit costs
    const baseCosts = calculateChamberCost(selectedProject, billStartDate, billEndDate, unbilledPeriodsData);
    
    if (!baseCosts || !baseCosts.unitCosts || !Array.isArray(baseCosts.unitCosts)) {
      return;
    }
    
    // Find the latest end date among all non-excluded chamber usage rows
    let latestEndDate = null;
    
    baseCosts.unitCosts
      .filter(unitCost => unitCost && !excludedChamberRows.includes(unitCost.uniqueKey))
      .forEach(unitCost => {
        // Check if there's an override for this row's end date
        const override = overrides[unitCost.uniqueKey];
        const endDate = override?.endDate || unitCost.endDate;
        
        if (endDate) {
          const dateObj = new Date(endDate);
          if (!latestEndDate || dateObj > latestEndDate) {
            latestEndDate = dateObj;
          }
        }
      });
    
    // Update bill end date if we found a valid latest date
    if (latestEndDate) {
      setBillEndDate(formatLocalDate(latestEndDate));
    }
  };

  // Handler for when bill end date is changed - updates all chamber usage end dates
  const handleBillEndDateChange = (newEndDateValue) => {
    // First, set the bill end date
    setBillEndDate(newEndDateValue);
    
    // If empty, don't update chamber usage dates
    if (!newEndDateValue) return;
    
    // Parse the new end date
    const [year, month, day] = newEndDateValue.split('-').map(Number);
    const newEndDate = new Date(year, month - 1, day, 12, 0, 0, 0);
    
    // Get the base chamber costs to access all unit costs
    const baseCosts = calculateChamberCost(selectedProject, billStartDate, billEndDate, unbilledPeriodsData);
    
    if (!baseCosts || !baseCosts.unitCosts || !Array.isArray(baseCosts.unitCosts)) {
      return;
    }
    
    // Update chamber usage end dates to match the new bill end date.
    //
    // IMPORTANT: only ACTIVE (ongoing) assignments are billed up to the bill
    // end date. A COMPLETED/Terminated assignment has a fixed end date (e.g. it
    // ended Jan 31) and must NOT be extended to the bill end date - otherwise we
    // would over-bill the client for days the assignment was not active. For
    // active assignments we still clamp to the assignment's own end date so we
    // never bill past when the assignment actually ends.
    const newOverrides = { ...chamberDateOverrides };
    
    baseCosts.unitCosts
      .filter(unitCost => unitCost && !excludedChamberRows.includes(unitCost.uniqueKey))
      .forEach(unitCost => {
        // Leave completed/terminated assignments at their existing end date.
        if (!isAssignmentActive(unitCost.assignmentStatus)) {
          return;
        }
        // Clamp active assignments so we never bill past their real end date.
        const clampedEnd = clampEndDateToAssignment(newEndDate, unitCost.assignmentEndDate);
        // Preserve existing startDate override if any, update endDate
        newOverrides[unitCost.uniqueKey] = {
          ...newOverrides[unitCost.uniqueKey],
          endDate: clampedEnd
        };
      });
    
    setChamberDateOverrides(newOverrides);
  };

  // Calculate chamber cost with date overrides
  const calculateChamberCostWithOverrides = () => {
    const baseCosts = calculateChamberCost(selectedProject, billStartDate, billEndDate, unbilledPeriodsData);
    
    // Handle case where baseCosts or unitCosts is undefined
    if (!baseCosts || !baseCosts.unitCosts || !Array.isArray(baseCosts.unitCosts)) {
      return { unitCosts: [], totalCost: 0 };
    }
    
    // Apply date overrides and filter out excluded rows
    const modifiedCosts = baseCosts.unitCosts
      .filter(unitCost => unitCost && !excludedChamberRows.includes(unitCost.uniqueKey))
      .map(unitCost => {
        const override = chamberDateOverrides[unitCost.uniqueKey];
        if (override) {
          // Create new dates if overrides exist, with fallback to current date
          const startDate = override.startDate ? new Date(override.startDate) : (unitCost.startDate ? new Date(unitCost.startDate) : new Date());
          const endDate = override.endDate ? new Date(override.endDate) : (unitCost.endDate ? new Date(unitCost.endDate) : new Date());
          
          // Shared inclusive, DST-safe day calculation (single source of truth)
          const daysUsed = calculateDays(startDate, endDate);
          // Re-price across the new dates: moving a date can cross a price change.
          const priced = priceChamberRow(unitCost, startDate, endDate);
          
          return {
            ...unitCost,
            startDate: startDate,
            endDate: endDate,
            daysUsed: daysUsed,
            billedUnits: priced.units,
            rate: priced.rate,
            priceSegments: priced.segments,
            totalCost: priced.totalCost,
            isCustomized: true
          };
        }
        return unitCost;
      });

    const totalCost = modifiedCosts.reduce((sum, unit) => sum + (unit?.totalCost || 0), 0);
    return { unitCosts: modifiedCosts, totalCost };
  };

  const getDisplayChamberRows = () => {
    const { unitCosts, totalCost } = calculateChamberCostWithOverrides();
    // Only rows active across the whole bill period merge, so consolidation
    // needs the period the admin has set.
    return {
      unitCosts: consolidateChamberUsage(unitCosts, {
        billStart: billStartDate,
        billEnd: billEndDate
      }),
      totalCost
    };
  };

  // Handler to remove a chamber usage row from the invoice
  const handleRemoveChamberRow = (uniqueKeyOrKeys) => {
    const keys = Array.isArray(uniqueKeyOrKeys) ? uniqueKeyOrKeys : [uniqueKeyOrKeys];
    const newExcludedRows = [...excludedChamberRows];
    keys.forEach(key => {
      if (key && !newExcludedRows.includes(key)) newExcludedRows.push(key);
    });
    setExcludedChamberRows(newExcludedRows);
    updateBillEndDateAfterExclusion(newExcludedRows);
  };

  // Handler to restore a removed chamber usage row
  const handleRestoreChamberRow = (uniqueKey) => {
    const newExcludedRows = excludedChamberRows.filter(key => key !== uniqueKey);
    setExcludedChamberRows(newExcludedRows);
    // Update bill end date after restoring this row
    updateBillEndDateAfterExclusion(newExcludedRows);
  };
  
  // Helper to update bill dates when rows are excluded/restored
  const updateBillEndDateAfterExclusion = (newExcludedRows) => {
    const baseCosts = calculateChamberCost(selectedProject, billStartDate, billEndDate, unbilledPeriodsData);
    
    if (!baseCosts || !baseCosts.unitCosts || !Array.isArray(baseCosts.unitCosts)) {
      return;
    }
    
    // Find the earliest start date and latest end date among remaining (non-excluded) rows
    let earliestStartDate = null;
    let latestEndDate = null;
    
    baseCosts.unitCosts
      .filter(unitCost => unitCost && !newExcludedRows.includes(unitCost.uniqueKey))
      .forEach(unitCost => {
        const override = chamberDateOverrides[unitCost.uniqueKey];
        const startDate = override?.startDate || unitCost.startDate;
        const endDate = override?.endDate || unitCost.endDate;
        
        if (startDate) {
          const dateObj = new Date(startDate);
          if (!earliestStartDate || dateObj < earliestStartDate) {
            earliestStartDate = dateObj;
          }
        }
        
        if (endDate) {
          const dateObj = new Date(endDate);
          if (!latestEndDate || dateObj > latestEndDate) {
            latestEndDate = dateObj;
          }
        }
      });
    
    if (earliestStartDate) {
      setBillStartDate(formatLocalDate(earliestStartDate));
    }
    if (latestEndDate) {
      setBillEndDate(formatLocalDate(latestEndDate));
    }
  };

  // Format date for input field (yyyy-MM-dd) - handles timezone properly
  const formatDateForInput = (date) => {
    if (!date) return '';
    
    // Work directly with the date object in local timezone
    const d = new Date(date);
    
    // Use local date methods (these work in local timezone by default)
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Generate invoice
  const handleGenerateInvoice = async () => {
    try {
      // Amount distribution is now user-driven (set by the PI/payers after the
      // invoice is sent), so there is no admin-side split to validate here.
      // Get the total number of invoices for this project
      const invoiceResponse = await axios.get(`/api/invoices/by-project/${selectedProject._id}`);
      const existingInvoices = invoiceResponse.data || [];
      const nextInvoiceNumber = existingInvoices.length + 1;
      const formattedNumber = nextInvoiceNumber.toString().padStart(3, '0');
      const invoiceId = `${selectedProject.project_id}_INV${formattedNumber}`;

      const chamberCostDetails = calculateChamberCostWithOverrides();
      const resourceCostDetails = calculateResourceCost(selectedProject, billEndDate);
      
      // Get chamber details from project assignments (using new schema); omit when resource-only.
      // A row that spans a price change becomes one invoice line per price, so
      // every billed period carries the single price it was billed at.
      const chamberUsage = resourceOnly ? [] : (chamberCostDetails?.unitCosts || []).flatMap(unit => {
        const segments = priceChamberRow(unit, unit.startDate, unit.endDate).segments;
        return segments.map(segment => {
          // Ensure dates are set to noon to avoid timezone issues
          const startDate = new Date(segment.startDate);
          startDate.setHours(12, 0, 0, 0);
          const endDate = new Date(segment.endDate);
          endDate.setHours(12, 0, 0, 0);

          return {
            chamber_id: unit.chamberId,
            assignment_id: unit.assignmentId,  // MongoDB ObjectId of the assignment
            rate_id: unit.rateId,              // Rate ID (e.g., 'academic', 'federal')
            start_date: startDate.toISOString(),
            end_date: endDate.toISOString(),
            quantity: unit.quantity,
            total_days: segment.days,
            rate: segment.rate,
            rate_basis: unit.rateBasis,
            total_cost: segment.cost
          };
        });
      });

      const resourceUsage = includeResourceUsage
        ? (resourceCostDetails?.resources || []).map(res => ({
            resource_id: res.resourceId,
            assignment_id: res.assignmentId,
            quantity: res.quantity,
            rate: res.rate,
            total_cost: res.totalCost
          }))
        : [];

      // Calculate total cost
      const totalInvoiceAmount = calculateTotalBillAmount();
      
      // Validate that there's something to bill
      const resourceCostForValidation = includeResourceUsage ? calculateResourceCost(selectedProject, billEndDate).totalCost : 0;
      const chamberCostForValidation = resourceOnly ? 0 : (calculateChamberCostWithOverrides().totalCost || 0);
      const currentPeriodAmount = chamberCostForValidation +
                                  resourceCostForValidation +
                                  adjustments.reduce((sum, adj) => sum + (parseFloat(adj.amount) || 0), 0);
      const includedPreviousBalance = getIncludedPreviousBalanceTotal();
      
      if (currentPeriodAmount <= 0 && includedPreviousBalance <= 0) {
        toast.error('Cannot generate invoice with zero amount. No billable usage found for the selected period.');
        return;
      }

      // Prepare previous balance data
      const previousBalanceData = previousBalances
        .filter(balance => balance.included)
        .map(balance => ({
          invoice_id: balance.invoice_id,
          original_amount: balance.original_amount,
          amount_paid: balance.amount_paid,
          remaining_amount: balance.remaining_amount
        }));

      // The AR account is the project-phase that receives the revenue on the
      // business-office billing request, so it must be chosen before generating.
      if (!accountMapping.facility_id || !accountMapping.account_number) {
        toast.error('Select the Accounts Receivable facility and account that will receive this revenue.');
        return;
      }
      const accountMappingData = {
        facility_id: accountMapping.facility_id,
        account_number: accountMapping.account_number
      };

      // Format dates properly for backend (yyyy-MM-dd format)
      // If already in YYYY-MM-DD format, return as-is to avoid timezone issues
      const formatDateForBackend = (dateString) => {
        if (!dateString) return '';
        // If it's already in YYYY-MM-DD format, return as-is
        if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
          return dateString;
        }
        // Otherwise, use the formatLocalDate helper to avoid timezone shift
        return formatLocalDate(dateString);
      };

      // Create the invoice
      const response = await axios.post('/api/invoices', {
        project_id: selectedProject.project_id,
        bill_start_date: formatDateForBackend(billStartDate),
        bill_end_date: formatDateForBackend(billEndDate),
        bill_to: billTo,
        comment: invoiceComment,
        chamber_usage: chamberUsage,
        resource_usage: resourceUsage,
        total_amount: totalInvoiceAmount,
        adjustments: adjustments,
        previous_balance: previousBalanceData,
        // Distribution is no longer set by admins; the PI/payers set it once the
        // invoice is sent to users. The invoice is created as a Draft.
        account_mapping: accountMappingData
      });

      if (response.data) {
        onInvoiceCreated(response.data);
        onClose();
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      
      if (error.response?.status === 400) {
        const errorData = error.response.data;
        if (errorData.conflicting_invoice) {
          toast.error(`${errorData.message}`, {
            autoClose: 8000,
          });
        } else {
          toast.error(errorData.message || 'Invalid invoice data');
        }
      } else {
        toast.error('Failed to generate invoice: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setAdjustments([]);
      setUserDistributions([]);
      setAccountDistributions([]);
      setDistributionType('user_based');
      setBillTo({
        name: '',
        company: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        email: ''
      });
      setSelectedBillToUser('');
      setInvoiceComment('');
      setBillStartDate('');
      setBillEndDate('');
      setDisplayMode('form');
      setCreatedInvoice(null);
      // Reset account-related states
      setSelectedUserForNewAccount('');
      setSelectedUserAccounts([]);
      setSelectedAccountId('');
      setNewAccountNumber('');
      setNewSourceOfFunds('');
      setIsNewAccount(false);
      setNewlyAddedUsers([]);
      setOriginalProjectUsers([]);
      // Reset chamber date overrides and excluded rows
      setEditableChamberUsage({});
      setChamberDateOverrides({});
      setExcludedChamberRows([]);
    }
  }, [open]);

  // The selectedProject should already have the necessary data populated
  // We'll use the existing calculation functions that work with the selectedProject directly

  // Guard clause: Don't render if no valid project is selected
  if (!selectedProject || !selectedProject._id) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Generate Invoice</DialogTitle>
        <DialogContent>
          <Typography>Please select a project first.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="invoice-dialog-title"
      disableEnforceFocus
      disableRestoreFocus
      PaperProps={{
        sx: {
          minHeight: '90vh',
          maxHeight: '95vh',
          margin: '20px',
          maxWidth: '1200px'
        }
      }}
    >
      <DialogTitle 
        id="invoice-dialog-title"
        sx={{
          padding: '20px 32px',
          borderBottom: '3px solid var(--brand-primary, #c41e3a)',
          background: 'linear-gradient(135deg, var(--brand-primary, #c41e3a) 0%, var(--brand-primary-hover, #a01830) 100%)',
          color: 'var(--brand-on-primary, #fff)',
          '& .MuiTypography-root': { color: 'var(--brand-on-primary, #fff)' }
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--brand-on-primary, #fff)' }}>
          {displayMode === 'form' ? 'Generate New Invoice' : 'Invoice Created'}
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--brand-on-primary, #fff)', opacity: 0.85, mt: 0.5 }}>
          {displayMode === 'form' ? 'Create a new billing invoice for the selected project' : `Invoice Number: ${getInvoiceNumber(createdInvoice?.invoice_id)}`}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ padding: 0, overflow: 'auto', backgroundColor: '#fafbfc' }}>
        {displayMode === 'summary' && createdInvoice ? (
          // Post-invoice creation UI as a dropdown summary
          <Box>
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom color="primary">
                  Invoice Generated Successfully
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1"><strong>Invoice Number:</strong> {getInvoiceNumber(createdInvoice.invoice_id)}</Typography>
                    <Typography variant="body1"><strong>Generated:</strong> {new Date(createdInvoice.generation_date).toLocaleDateString()}</Typography>
                    <Typography variant="body1">
                      <strong>Billing Period:</strong>{' '}
                      {createdInvoice.billing_period 
                        ? `${fmtUTCDate(createdInvoice.billing_period.start_date)} - ${fmtUTCDate(createdInvoice.billing_period.end_date)}` 
                        : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1"><strong>Total Amount:</strong> ${createdInvoice.total_amount?.toFixed(2)}</Typography>
                    <Typography variant="body1">
                      <strong>Status:</strong>{' '}
                      <Chip 
                        size="small"
                        label={createdInvoice.status_history?.[createdInvoice.status_history.length - 1]?.status || 'Draft'} 
                        color="primary"
                      />
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button 
                  startIcon={<VisibilityIcon />} 
                  onClick={handleViewPDF}
                  variant="outlined"
                >
                  View PDF
                </Button>
                <Button 
                  startIcon={<DownloadIcon />} 
                  onClick={handleDownloadPDF}
                  variant="outlined"
                >
                  Download PDF
                </Button>
                <Button 
                  startIcon={<PaymentIcon />} 
                  onClick={handleShowRecordPayment}
                  variant="outlined" 
                  color="success"
                >
                  Record Payment
                </Button>
                <Button 
                  startIcon={<ReceiptLongIcon />} 
                  onClick={handleShowInvoiceDetail}
                  variant="outlined" 
                  color="info"
                >
                  Invoice Details
                </Button>
              </CardActions>
            </Card>
            
            {/* Invoice Detail Dialog */}
            {showInvoiceDetail && (
              <InvoiceDetail
                open={showInvoiceDetail}
                onClose={() => setShowInvoiceDetail(false)}
                invoiceId={createdInvoice._id}
                selectedInvoice={createdInvoice}
                onUpdate={(updatedInvoice) => setCreatedInvoice(updatedInvoice)}
              />
            )}
            
            {/* Record Payment Dialog */}
            {showRecordPayment && (
              <RecordPayment
                open={showRecordPayment}
                onClose={() => setShowRecordPayment(false)}
                invoice={createdInvoice}
                onPaymentRecorded={handlePaymentRecorded}
              />
            )}
          </Box>
        ) : (
          // Original invoice creation UI
          <Box component="form" sx={{ 
            padding: '24px 32px',
            backgroundColor: '#fafbfc',
            '& .MuiTextField-root': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#adb5bd',
                borderWidth: '1.5px',
              },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--brand-primary, #c41e3a)',
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
              }
            }
          }}>
            {/* Project Header */}
            <Paper 
              elevation={0}
              sx={{ 
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                border: '2px solid #dee2e6',
                borderRadius: '10px',
                padding: '20px 28px',
                marginBottom: '28px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              <Box>
                <Typography variant="overline" sx={{ color: '#6c757d', letterSpacing: 1, display: 'block', mb: 0.5 }}>
                  Project
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#212529' }}>
                  {selectedProject?.project?.project_title || selectedProject?.project_title || 'Untitled Project'}
                </Typography>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                gap: 3, 
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }}>Proposal ID</Typography>
                  <Typography sx={{ 
                    fontFamily: '"Courier New", monospace', 
                    backgroundColor: '#e9ecef', 
                    padding: '4px 12px', 
                    borderRadius: '4px',
                    fontWeight: 500
                  }}>
                    {selectedProject?.project_id}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }}>Status</Typography>
                  <Chip 
                    label={selectedProject?.project?.project_status || 'Active'} 
                    size="small" 
                    color="success"
                    sx={{ fontWeight: 500 }}
                  />
                </Box>
              </Box>
            </Paper>

            {/* Invoice Details Section */}
            <Paper elevation={0} sx={{ 
              background: 'white',
              border: '1.5px solid #dee2e6',
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <Typography variant="h6" sx={{ 
                color: 'var(--brand-primary-text, #c41e3a)', 
                fontWeight: 600, 
                marginBottom: '20px',
                paddingLeft: '12px',
                borderLeft: '4px solid var(--brand-primary, #c41e3a)'
              }}>
                Invoice Details
              </Typography>
              <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Generation Date"
                  value={formatLocalDate(new Date())}
                  disabled
                />
              </Grid>
              
              {/* Billing Period */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Bill Start Date"
                  InputLabelProps={{ shrink: true }}
                  value={billStartDate}
                  onChange={(e) => setBillStartDate(e.target.value)}
                  error={!billStartDate}
                  helperText={!billStartDate ? 'Start date is required' : ''}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Bill End Date"
                  InputLabelProps={{ shrink: true }}
                  value={billEndDate}
                  onChange={(e) => handleBillEndDateChange(e.target.value)}
                  error={!billEndDate}
                  helperText={!billEndDate ? 'End date is required' : 'Changing this updates all compartment usage end dates'}
                />
              </Grid>

              </Grid>
            </Paper>

            {/* Horizontal Divider */}
            <Box sx={{ height: '1px', background: 'linear-gradient(to right, var(--brand-primary, #c41e3a), #e0e0e0, transparent)', my: 3 }} />

            {/* Bill To Section */}
            <Paper elevation={0} sx={{ 
              background: 'white',
              border: '1.5px solid #dee2e6',
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <Typography variant="h6" sx={{ 
                color: 'var(--brand-primary-text, #c41e3a)', 
                fontWeight: 600, 
                marginBottom: '20px',
                paddingLeft: '12px',
                borderLeft: '4px solid var(--brand-primary, #c41e3a)'
              }}>
                Bill To
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required error={!selectedBillToUser}>
                    <InputLabel>Select Recipient</InputLabel>
                    <Select
                      value={selectedBillToUser}
                      onChange={(e) => handleBillToUserSelect(e.target.value)}
                      label="Select Recipient"
                      disabled={loadingBillToUser}
                    >
                      <MenuItem value="">
                        <em>Select a project user</em>
                      </MenuItem>
                      {(selectedProject?.users || [])
                        .filter(u => !u.isDeleted)
                        .map((user, index) => (
                          <MenuItem key={index} value={user.email}>
                            {user.first_name} {user.last_name}
                          </MenuItem>
                        ))}
                    </Select>
                    {!selectedBillToUser && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        Please select a recipient
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Name (auto-filled)"
                    value={billTo.name}
                    onChange={(e) => setBillTo({ ...billTo, name: e.target.value })}
                    helperText="Editable if needed"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputLabel shrink>Phone Number</InputLabel>
                  <PhoneInput
                    value={billTo.phone}
                    onChange={(v) => setBillTo({ ...billTo, phone: v })}
                  />
                  <FormHelperText>Auto-filled from user's profile</FormHelperText>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    value={billTo.email}
                    onChange={(e) => setBillTo({ ...billTo, email: e.target.value })}
                    helperText="Auto-filled from selected user"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Company/Organization"
                    value={billTo.company}
                    onChange={(e) => setBillTo({ ...billTo, company: e.target.value })}
                    helperText="Auto-filled from user's company (defaults to NCSU or Commercial Client)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address Line 1"
                    value={billTo.address_line1}
                    onChange={(e) => setBillTo({ ...billTo, address_line1: e.target.value })}
                    helperText="Auto-filled from user's profile"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address Line 2"
                    value={billTo.address_line2}
                    onChange={(e) => setBillTo({ ...billTo, address_line2: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    label="City"
                    value={billTo.city}
                    onChange={(e) => setBillTo({ ...billTo, city: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="State"
                    value={billTo.state}
                    onChange={(e) => setBillTo({ ...billTo, state: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    value={billTo.zip}
                    onChange={(e) => setBillTo({ ...billTo, zip: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Paper>

            {!resourceOnly && (
            <>
            {/* Horizontal Divider */}
            <Box sx={{ height: '1px', background: 'linear-gradient(to right, var(--brand-primary, #c41e3a), #e0e0e0, transparent)', my: 3 }} />

            {/* Chamber Usage Section */}
            <Paper elevation={0} sx={{ 
              background: 'white',
              border: '1.5px solid #dee2e6',
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <Typography variant="h6" sx={{ 
                color: 'var(--brand-primary-text, #c41e3a)', 
                fontWeight: 600, 
                marginBottom: '12px',
                paddingLeft: '12px',
                borderLeft: '4px solid var(--brand-primary, #c41e3a)'
              }}>
                Compartment Usage
              </Typography>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: '12px 16px',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '6px',
                marginBottom: '16px'
              }}>
                <Typography variant="body2" sx={{ color: '#1e40af' }}>
                  💡 <strong>Tip:</strong> You can edit the start and end dates below to customize the billing period for each compartment assignment. 
                  Changes will automatically recalculate the days and cost.
                </Typography>
              </Box>
              <TableContainer component={Paper} elevation={0} sx={{ 
                border: '2px solid #dee2e6', 
                borderRadius: '8px', 
                overflow: 'hidden',
                '& .MuiTableHead-root': {
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                },
                '& .MuiTableCell-head': {
                  fontWeight: 600,
                  color: '#495057',
                  borderBottom: '2px solid #dee2e6',
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  letterSpacing: '0.5px'
                }
              }}>
                  <Table size="small" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Compartment</TableCell>
                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Start Date</TableCell>
                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>End Date</TableCell>
                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Days Used</TableCell>
                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Rate</TableCell>
                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Unit Type</TableCell>
                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Quantity</TableCell>
                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Total Cost</TableCell>
                        <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px', width: '60px' }}>Remove</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(getDisplayChamberRows()?.unitCosts || []).map((unitCost) => {
                        const sourceKeys = unitCost.sourceKeys?.length
                          ? unitCost.sourceKeys
                          : [unitCost.uniqueKey];
                        const datesEditable = sourceKeys.length === 1 && !unitCost.isConsolidated;
                        return (
                        <TableRow key={unitCost.uniqueKey} sx={{ backgroundColor: unitCost.isCustomized ? '#f3f4f6' : 'inherit' }}>
                          <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>
                            {unitCost.chamberName}
                            {isAssignmentActive(unitCost.assignmentStatus) ? (
                              <Chip label="Active" size="small" color="success" variant="outlined" sx={{ ml: 1 }} />
                            ) : (
                              <Chip label="Completed" size="small" color="default" variant="outlined" sx={{ ml: 1 }} />
                            )}
                            {unitCost.isCustomized && <Chip label="Custom" size="small" color="primary" sx={{ ml: 1 }} />}
                            {unitCost.isConsolidated && <Chip label="Merged" size="small" variant="outlined" sx={{ ml: 1 }} />}
                          </TableCell>
                          <TableCell sx={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                            {datesEditable ? (
                            <TextField
                              type="date"
                              size="small"
                              value={formatDateForInput(unitCost.startDate)}
                              onChange={(e) => handleChamberDateChange(sourceKeys[0], 'startDate', e.target.value)}
                              sx={{ 
                                '& .MuiInputBase-input': { 
                                  fontSize: '0.875rem',
                                  padding: '4px 8px'
                                },
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    borderColor: unitCost.isCustomized ? 'var(--brand-primary, #c41e3a)' : '#e0e0e0'
                                  }
                                }
                              }}
                            />
                            ) : formatDateForInput(unitCost.startDate)}
                          </TableCell>
                          <TableCell sx={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                            {datesEditable ? (
                            <TextField
                              type="date"
                              size="small"
                              value={formatDateForInput(unitCost.endDate)}
                              onChange={(e) => handleChamberDateChange(sourceKeys[0], 'endDate', e.target.value)}
                              sx={{ 
                                '& .MuiInputBase-input': { 
                                  fontSize: '0.875rem',
                                  padding: '4px 8px'
                                },
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    borderColor: unitCost.isCustomized ? 'var(--brand-primary, #c41e3a)' : '#e0e0e0'
                                  }
                                }
                              }}
                            />
                            ) : formatDateForInput(unitCost.endDate)}
                          </TableCell>
                          <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0', fontWeight: unitCost.isCustomized ? 600 : 400 }}>
                            {unitCost.daysUsed}
                            {formatBilledUnits(unitCost.rateBasis, unitCost.billedUnits) && (
                              <Typography variant="caption" color="textSecondary" display="block" sx={{ whiteSpace: 'nowrap' }}>
                                = {formatBilledUnits(unitCost.rateBasis, unitCost.billedUnits)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>
                            {(unitCost.priceSegments?.length || 0) > 1 ? (
                              <Box>
                                {unitCost.priceSegments.map(seg => (
                                  <Typography key={seg.startDate.toString()} variant="caption" display="block" sx={{ whiteSpace: 'nowrap' }}>
                                    {formatRate(seg.rate)} {formatRateBasis(unitCost.rateBasis)} × {seg.days}d
                                    <Typography component="span" variant="caption" color="textSecondary">
                                      {' '}from {formatDateForInput(seg.startDate)}
                                    </Typography>
                                  </Typography>
                                ))}
                                <Typography variant="caption" color="primary" display="block">
                                  Price change: billed as {unitCost.priceSegments.length} lines
                                </Typography>
                              </Box>
                            ) : (
                              <>{formatRate(unitCost.rate)} {formatRateBasis(unitCost.rateBasis)}</>
                            )}
                          </TableCell>
                          <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{unitCost.unitName}</Typography>
                              <Typography variant="caption" color="textSecondary">{unitCost.rateName}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>{unitCost.quantity}</TableCell>
                          <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0', fontWeight: unitCost.isCustomized ? 600 : 400 }}>${(unitCost.totalCost || 0).toFixed(2)}</TableCell>
                          <TableCell sx={{ padding: '8px', borderBottom: '1px solid #e0e0e0', textAlign: 'center' }}>
                            <IconButton aria-label="Remove from this invoice" 
                              size="small" 
                              color="error" 
                              onClick={() => handleRemoveChamberRow(sourceKeys)}
                              title="Remove from this invoice"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell colSpan={8} align="right" sx={{ fontWeight: 'bold', padding: '12px', borderBottom: 'none' }}>
                          <strong>Total Compartment Cost:</strong>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', padding: '12px', borderBottom: 'none' }}>
                          <strong>
                            ${(calculateChamberCostWithOverrides()?.totalCost || 0).toFixed(2)}
                          </strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Show excluded/removed rows with restore option */}
                {excludedChamberRows.length > 0 && (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff3e0', borderRadius: '4px', border: '1px solid #ff9800' }}>
                    <Typography variant="subtitle2" sx={{ color: '#e65100', mb: 1 }}>
                      ⚠️ Excluded from this invoice ({excludedChamberRows.length} period{excludedChamberRows.length > 1 ? 's' : ''}):
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 1, fontStyle: 'italic' }}>
                      These unbilled periods will remain unbilled and can be included in a future invoice.
                    </Typography>
                    {calculateChamberCost(selectedProject, billStartDate, billEndDate, unbilledPeriodsData)?.unitCosts
                      ?.filter(uc => uc && excludedChamberRows.includes(uc.uniqueKey))
                      ?.map(excluded => (
                        <Box key={excluded.uniqueKey} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            color="warning"
                            onClick={() => handleRestoreChamberRow(excluded.uniqueKey)}
                          >
                            Restore
                          </Button>
                          <Typography variant="body2">
                            {excluded.chamberName} - {excluded.unitName} ({formatLocalDate(excluded.startDate)} to {formatLocalDate(excluded.endDate)})
                          </Typography>
                        </Box>
                      ))
                    }
                  </Box>
                )}
            </Paper>

            {/* Horizontal Divider */}
            <Box sx={{ height: '1px', background: 'linear-gradient(to right, var(--brand-primary, #c41e3a), #e0e0e0, transparent)', my: 3 }} />
            </>
            )}

            {/* Resource Usage Section */}
            <Paper elevation={0} sx={{ 
              background: 'white',
              border: '1.5px solid #dee2e6',
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '24px',
              opacity: includeResourceUsage ? 1 : 0.85
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, marginBottom: 2 }}>
                <Typography variant="h6" sx={{ 
                  color: 'var(--brand-primary-text, #c41e3a)', 
                  fontWeight: 600, 
                  paddingLeft: '12px',
                  borderLeft: '4px solid var(--brand-primary, #c41e3a)'
                }}>
                  Resource Usage
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeResourceUsage}
                      onChange={(e) => setIncludeResourceUsage(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Include resource usage in this invoice"
                />
              </Box>
              {!includeResourceUsage && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Resource usage will not be included. Unbilled resources will remain available for the next invoice.
                </Typography>
              )}
              <TableContainer component={Paper} elevation={0} sx={{ 
                border: '2px solid #dee2e6', 
                borderRadius: '8px', 
                overflow: 'hidden'
              }}>
                <Table size="small" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                  <TableHead sx={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, padding: '14px 16px', borderBottom: '2px solid #dee2e6', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', color: '#495057' }}>Resource Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, padding: '14px 16px', borderBottom: '2px solid #dee2e6', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', color: '#495057' }}>Rate Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, padding: '14px 16px', borderBottom: '2px solid #dee2e6', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', color: '#495057' }}>Units</TableCell>
                      <TableCell sx={{ fontWeight: 600, padding: '14px 16px', borderBottom: '2px solid #dee2e6', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', color: '#495057' }}>Rate/Unit</TableCell>
                      <TableCell sx={{ fontWeight: 600, padding: '14px 16px', borderBottom: '2px solid #dee2e6', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', color: '#495057' }}>Total Cost</TableCell>
                    </TableRow>
                  </TableHead>
                    <TableBody>
                      {(calculateResourceCost(selectedProject, billEndDate)?.resources || []).map((resource, index) => (
                        <TableRow key={resource.uniqueKey}>
                          <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>{resource.resourceName}</TableCell>
                          <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>{resource.rateName || '—'}</TableCell>
                          <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>{resource.quantity != null ? `${resource.quantity} (${resource.rateBasis || 'unit'})` : '—'}</TableCell>
                          <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>${(resource.rate || 0).toFixed(2)}</TableCell>
                          <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>${(resource.totalCost || 0).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold', padding: '12px', borderBottom: 'none' }}>
                          <strong>Total Resource Cost:</strong>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', padding: '12px', borderBottom: 'none' }}>
                          <strong>
                            ${(calculateResourceCost(selectedProject, billEndDate)?.totalCost || 0).toFixed(2)}
                          </strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
            </Paper>

            {/* Horizontal Divider */}
            <Box sx={{ height: '1px', background: 'linear-gradient(to right, var(--brand-primary, #c41e3a), #e0e0e0, transparent)', my: 3 }} />

            {/* Adjustments Section */}
            <Paper elevation={0} sx={{ 
              background: 'white',
              border: '1.5px solid #dee2e6',
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <Typography variant="h6" sx={{ 
                  color: 'var(--brand-primary-text, #c41e3a)', 
                  fontWeight: 600,
                  paddingLeft: '12px',
                  borderLeft: '4px solid var(--brand-primary, #c41e3a)'
                }}>
                  Adjustments
                </Typography>
                <Button 
                  variant="contained" 
                  size="small" 
                  onClick={handleAddAdjustment}
                  sx={{ 
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, var(--brand-primary, #c41e3a) 0%, var(--brand-primary-hover, #a01830) 100%)',
                    boxShadow: '0 2px 4px var(--brand-focus-ring, rgba(196, 30, 58, 0.4))',
                  }}
                >
                  + Add Adjustment
                </Button>
              </Box>
              {adjustments.length === 0 && (
                <Typography variant="body2" sx={{ color: '#6c757d', fontStyle: 'italic', py: 2 }}>
                  No adjustments added. Click the button above to add an adjustment.
                </Typography>
              )}
              {(adjustments || []).map((adj, index) => (
                <Box key={index} sx={{ 
                  display: 'flex', 
                  gap: '16px', 
                  marginTop: '12px', 
                  padding: '16px 20px', 
                  backgroundColor: '#ffffff',
                  border: '1.5px solid #dee2e6',
                  borderRadius: '8px',
                  alignItems: 'center',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    borderColor: '#adb5bd',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
                  }
                }}>
                    <TextField
                      label="Amount"
                      type="text"
                      value={adj.amount}
                      onChange={(e) => handleAdjustmentChange(index, 'amount', e.target.value)}
                      onBlur={(e) => {
                        if (e.target.value === '') {
                          handleAdjustmentChange(index, 'amount', '0');
                        }
                      }}
                      inputProps={{ 
                        inputMode: 'decimal',
                        pattern: '[0-9]*\\.?[0-9]*',
                        placeholder: "0.00",
                        style: { textAlign: 'right' }
                      }}
                      sx={{ width: '200px' }}
                    />
                    <TextField
                      label="Comments"
                      value={adj.comments}
                      onChange={(e) => handleAdjustmentChange(index, 'comments', e.target.value)}
                      sx={{ flexGrow: 1 }}
                    />
                    <IconButton aria-label="Delete" onClick={() => handleRemoveAdjustment(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
            </Paper>

            {/* Horizontal Divider */}
            <Box sx={{ height: '1px', background: 'linear-gradient(to right, var(--brand-primary, #c41e3a), #e0e0e0, transparent)', my: 3 }} />

            {/* Invoice Comment Section */}
            <Paper elevation={0} sx={{ 
              background: 'white',
              border: '1.5px solid #dee2e6',
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <Typography variant="h6" sx={{ 
                color: 'var(--brand-primary-text, #c41e3a)', 
                fontWeight: 600, 
                marginBottom: '12px',
                paddingLeft: '12px',
                borderLeft: '4px solid var(--brand-primary, #c41e3a)'
              }}>
                Invoice Comment
              </Typography>
              <Typography variant="body2" sx={{ marginBottom: '16px', color: '#6c757d' }}>
                Add a comment that will appear at the bottom of the invoice PDF.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Comment"
                placeholder="e.g., Billing does not include space that may have become occupied after the billing period."
                value={invoiceComment}
                onChange={(e) => setInvoiceComment(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    '& fieldset': {
                      borderColor: '#adb5bd',
                      borderWidth: '2px',
                    },
                    '&:hover fieldset': {
                      borderColor: 'var(--brand-primary, #c41e3a)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'var(--brand-primary, #c41e3a)',
                      boxShadow: '0 0 0 3px var(--brand-primary-translucent, rgba(196, 30, 58, 0.12))',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 500,
                  }
                }}
              />
            </Paper>

            {/* Previous Unpaid Balances with Admin Controls */}
            {previousBalances.length > 0 && (
              <>
                {/* Horizontal Divider */}
                <Box sx={{ height: '1px', background: 'linear-gradient(to right, #fbbf24, #e0e0e0, transparent)', my: 3 }} />

                <Paper elevation={0} sx={{ 
                  background: '#fffbeb',
                  border: '2px solid #fbbf24',
                  borderRadius: '8px',
                  padding: '24px',
                  marginBottom: '24px'
                }}>
                  <Typography variant="h6" sx={{ 
                    color: '#92400e', 
                    fontWeight: 600, 
                    marginBottom: '12px',
                    paddingLeft: '12px',
                    borderLeft: '4px solid #f59e0b'
                  }}>
                    ⚠️ Previous Unpaid Balances
                  </Typography>
                  <Typography variant="body2" sx={{ marginBottom: '16px', color: '#78350f' }}>
                    The following unpaid invoices can be included in this new invoice. Check the ones you want to include.
                  </Typography>
                  <TableContainer component={Paper} elevation={0} sx={{ 
                    border: '2px solid #fcd34d', 
                    borderRadius: '8px', 
                    overflow: 'hidden',
                    background: 'white'
                  }}>
                    <Table size="small" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Include</TableCell>
                          <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Invoice #</TableCell>
                          <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Generation Date</TableCell>
                          <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Billing Period</TableCell>
                          <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Status</TableCell>
                          <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Original Amount</TableCell>
                          <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Amount Paid</TableCell>
                          <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Remaining Balance</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(previousBalances || []).map((balance, index) => (
                          <TableRow 
                            key={`balance-${index}-${balance.invoice_id}`}
                            sx={{ 
                              backgroundColor: balance.included ? 'inherit' : '#f5f5f5',
                              opacity: balance.included ? 1 : 0.6
                            }}
                          >
                            <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>
                              <input
                                type="checkbox"
                                checked={balance.included}
                                onChange={(e) => handlePreviousBalanceToggle(balance.invoice_id, e.target.checked)}
                                style={{ transform: 'scale(1.2)' }}
                              />
                            </TableCell>
                            <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>{getInvoiceNumber(balance.invoice_id)}</TableCell>
                            <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>{balance.generation_date_formatted}</TableCell>
                            <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>{balance.billing_period_formatted}</TableCell>
                            <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>
                              <Chip 
                                label={balance.status} 
                                size="small"
                                color={balance.status === 'Paid' ? 'success' : balance.status === 'Draft' ? 'default' : 'warning'}
                              />
                            </TableCell>
                            <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>${(balance.original_amount || 0).toFixed(2)}</TableCell>
                            <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>${(balance.amount_paid || 0).toFixed(2)}</TableCell>
                            <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>${(balance.remaining_amount || 0).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={7} align="right" sx={{ fontWeight: 'bold', padding: '12px', borderBottom: 'none' }}>
                            <strong>Total Included Previous Balance:</strong>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold', padding: '12px', borderBottom: 'none' }}>
                            <strong>
                              ${(getIncludedPreviousBalanceTotal() || 0).toFixed(2)}
                            </strong>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </>
            )}

            {/* Horizontal Divider */}
            <Box sx={{ height: '2px', background: 'linear-gradient(to right, #10b981, #e0e0e0, transparent)', my: 3 }} />

            {/* Bill Summary */}
            <Paper elevation={0} sx={{ 
              background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
              border: '2px solid #10b981',
              borderRadius: '12px',
              padding: '28px',
              marginBottom: '24px'
            }}>
              <Typography variant="h6" sx={{ 
                color: '#065f46', 
                fontWeight: 700, 
                marginBottom: '20px',
                paddingLeft: '12px',
                borderLeft: '4px solid #10b981',
                fontSize: '1.2rem'
              }}>
                💰 Bill Summary
              </Typography>
              {!resourceOnly && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px', padding: '8px 0' }}>
                <Typography sx={{ color: '#374151' }}>Compartment Usage Cost:</Typography>
                <Typography sx={{ fontWeight: 500 }}>
                  ${(calculateChamberCostWithOverrides()?.totalCost || 0).toFixed(2)}
                </Typography>
              </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px', padding: '8px 0' }}>
                <Typography sx={{ color: '#374151' }}>Resource Usage Cost:</Typography>
                <Typography sx={{ fontWeight: 500 }}>
                  ${includeResourceUsage ? (calculateResourceCost(selectedProject, billEndDate)?.totalCost || 0).toFixed(2) : '0.00'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px', padding: '8px 0' }}>
                <Typography sx={{ color: '#374151' }}>Adjustments:</Typography>
                <Typography sx={{ 
                  fontWeight: 500,
                  color: adjustments.reduce((total, adj) => total + (parseFloat(adj.amount) || 0), 0) < 0 ? '#dc2626' : '#16a34a'
                }}>
                  ${adjustments.reduce((total, adj) => total + (parseFloat(adj.amount) || 0), 0).toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px', padding: '8px 0' }}>
                <Typography sx={{ color: '#374151' }}>Previous Balances (Included):</Typography>
                <Typography sx={{ fontWeight: 500 }}>
                  ${(getIncludedPreviousBalanceTotal() || 0).toFixed(2)}
                </Typography>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginTop: '16px', 
                padding: '16px 20px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: '8px',
                color: 'white'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Total Bill Amount:</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  ${(calculateTotalBillAmount() || 0).toFixed(2)}
                </Typography>
              </Box>
            </Paper>

            {/* Horizontal Divider */}
            <Box sx={{ height: '1px', background: 'linear-gradient(to right, var(--brand-primary, #c41e3a), #e0e0e0, transparent)', my: 3 }} />

            {/* Payment Distribution is now user-driven: the project PI and payers
                select accounts and percentage splits after the invoice is sent.
                Admins no longer set the split here. */}
            <Paper elevation={0} sx={{ 
              background: 'white',
              border: '1.5px solid #dee2e6',
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <Typography variant="h6" sx={{ 
                color: 'var(--brand-primary-text, #c41e3a)', 
                fontWeight: 600, 
                marginBottom: '8px',
                paddingLeft: '12px',
                borderLeft: '4px solid var(--brand-primary, #c41e3a)'
              }}>
                Payment Distribution
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ pl: '12px' }}>
                Accounts and percentage splits are selected by the project PI and payers
                after the invoice is finalized and sent to users. You no longer set them here.
              </Typography>
            </Paper>

            {false && (
            <Paper elevation={0} sx={{ 
              background: 'white',
              border: '1.5px solid #dee2e6',
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <Typography variant="h6" sx={{ 
                color: 'var(--brand-primary-text, #c41e3a)', 
                fontWeight: 600, 
                marginBottom: '20px',
                paddingLeft: '12px',
                borderLeft: '4px solid var(--brand-primary, #c41e3a)'
              }}>
                Amount Distribution
              </Typography>
                <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '16px' }}>
                  <Button 
                    variant={distributionType === 'user_based' ? 'contained' : 'outlined'}
                    size="medium" 
                    onClick={() => setDistributionType('user_based')}
                  >
                    User Split
                  </Button>
                  <Button 
                    variant={distributionType === 'account_based' ? 'contained' : 'outlined'}
                    size="medium" 
                    onClick={() => setDistributionType('account_based')}
                  >
                    Account Split
                  </Button>
                </Box>
                {distributionType === 'user_based' ? (
                  // User-based distribution table
                  <>
                    <TableContainer component={Paper} sx={{ margin: '16px 0', border: '1px solid #e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
                      <Table size="small" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Status</TableCell>
                            <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Name</TableCell>
                            <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Email</TableCell>
                            <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Department</TableCell>
                            <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Role</TableCell>
                            <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Percentage Share (%)</TableCell>
                            <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Amount Share ($)</TableCell>
                            <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(userDistributions || []).map((dist, index) => {
                            // Ensure user_id is a string ID, not an object
                            const userId = typeof dist.user_id === 'string' 
                              ? dist.user_id 
                              : (dist.user_id?._id || `user-${index}`);
                            
                            const isOriginalUser = originalProjectUsers.includes(userId);
                            
                            return (
                              <TableRow 
                                key={`user-dist-${userId}-${index}`}
                                sx={{ 
                                  backgroundColor: isOriginalUser ? 'inherit' : '#fff8e1', // Light yellow for added users
                                  borderLeft: isOriginalUser ? 'none' : '3px solid #ff9800' // Orange left border for added users
                                }}
                              >
                                <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>
                                  {isOriginalUser ? (
                                    <Chip 
                                      label="Original" 
                                      size="small" 
                                      color="primary" 
                                      variant="outlined"
                                    />
                                  ) : (
                                    <Chip 
                                      label="Added" 
                                      size="small" 
                                      color="warning"
                                      sx={{ backgroundColor: '#ff9800', color: 'white' }}
                                    />
                                  )}
                                </TableCell>
                                <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>{dist.user_name}</TableCell>
                                <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>{dist.email}</TableCell>
                                <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>{dist.department || 'N/A'}</TableCell>
                                <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>
                                  {typeof dist.role === 'object' && dist.role !== null 
                                    ? (dist.role.role || 'N/A') 
                                    : (dist.role || 'N/A')}
                                </TableCell>
                                <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>
                                  <TextField
                                    type="text"
                                    value={dist.percent_share}
                                    onChange={(e) => handleUserDistributionChange(userId, e.target.value)}
                                    onBlur={(e) => {
                                      if (e.target.value === '') {
                                        handleUserDistributionChange(userId, '0');
                                      }
                                    }}
                                    inputProps={{ 
                                      inputMode: 'decimal',
                                      pattern: '[0-9]*\\.?[0-9]*',
                                      style: { textAlign: 'right' }
                                    }}
                                    size="small"
                                    sx={{ width: '100px' }}
                                  />
                                </TableCell>
                                <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>
                                  ${(((calculateTotalBillAmount() || 0) * (dist.percent_share || 0)) / 100).toFixed(2)}
                                </TableCell>
                                <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>
                                  {!isOriginalUser ? (
                                    <IconButton aria-label="Remove user from project (Added user)" 
                                      size="small" 
                                      onClick={() => {
                                        if (window.confirm(`Are you sure you want to remove ${dist.user_name} from this project? This action cannot be undone.`)) {
                                          handleRemoveNewlyAddedUser(userId);
                                        }
                                      }}
                                      color="error"
                                      title="Remove user from project (Added user)"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  ) : (
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        color: '#666', 
                                        fontStyle: 'italic',
                                        fontSize: '11px'
                                      }}
                                    >
                                      Original member
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {/* Add User Row */}
                          <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                            <TableCell colSpan={8} sx={{ padding: '16px', borderBottom: '1px solid #e0e0e0' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', minWidth: '120px' }}>
                                  Add User to Project:
                                </Typography>
                                <select
                                  value=""
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      handleAddUserToProject(e.target.value);
                                    }
                                  }}
                                  style={{
                                    flex: 1,
                                    padding: '8px',
                                    fontSize: '14px',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    backgroundColor: 'white'
                                  }}
                                  onFocus={() => fetchAllUsers()}
                                >
                                  <option value="">Select a user to add to the project</option>
                                  {allUsers
                                    .filter(user => {
                                      // Filter out users already in distributions
                                      const distUserIds = (userDistributions || []).map(dist => {
                                        return typeof dist.user_id === 'string' 
                                          ? dist.user_id 
                                          : dist.user_id?._id;
                                      });
                                      return !distUserIds.includes(user._id);
                                    })
                                    .map((user) => (
                                      <option key={user._id} value={user._id}>
                                        {user.first_name} {user.last_name} ({user.email})
                                      </option>
                                    ))}
                                </select>
                              </Box>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={6} align="right" sx={{ fontWeight: 'bold', padding: '12px', borderBottom: 'none' }}>
                              <strong>Total:</strong>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', padding: '12px', borderBottom: 'none' }}>
                              <strong>
                                {((userDistributions || []).reduce((sum, dist) => {
                                  const value = dist.percent_share === '' ? 0 : Number(dist.percent_share || 0);
                                  return sum + value;
                                }, 0)).toFixed(2)}%
                              </strong>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', padding: '12px', borderBottom: 'none' }}>
                              <strong>${(calculateTotalBillAmount() || 0).toFixed(2)}</strong>
                            </TableCell>
                            <TableCell sx={{ padding: '12px', borderBottom: 'none' }}></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {userDistributions.reduce((sum, dist) => {
                      const value = dist.percent_share === '' ? 0 : Number(dist.percent_share);
                      return sum + value;
                    }, 0) !== 100 && (
                      <Typography sx={{ color: '#d32f2f', marginTop: '8px' }}>
                        Total percentage must equal 100%
                      </Typography>
                    )}
                    
                    {/* User Management Info */}
                    <Box sx={{ 
                      marginTop: '16px', 
                      padding: '12px', 
                      backgroundColor: '#e3f2fd', 
                      borderRadius: '4px',
                      border: '1px solid var(--brand-primary, #c41e3a)'
                    }}>
                      <Typography variant="body2" sx={{ color: 'var(--brand-primary-text, #c41e3a)', fontWeight: 'bold', marginBottom: '4px' }}>
                        ℹ️ User Management:
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'var(--brand-primary-text, #c41e3a)', fontSize: '12px' }}>
                        • <strong>Original</strong> users are from the project proposal and cannot be removed here<br/>
                        • <strong>Added</strong> users can be removed using the delete button<br/>
                        • For advanced user management, use the Applications page in the admin panel
                      </Typography>
                    </Box>
                  </>
                ) : (
                  // Account-based distribution table
                  <>
                    <Box sx={{ marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center' }}>
                      <Typography variant="body2">Available Accounts from Project:</Typography>
                      {selectedProject?.funding?.map((funding, index) => (
                        <Button
                          key={`funding-${index}`}
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            const projectUser = funding.user_email 
                              ? selectedProject.users?.find(u => u.email === funding.user_email)
                              : (funding.user_id ? selectedProject.users?.find(u => (u.user?._id || u.user)?.toString() === funding.user_id?.toString()) : null);
                            const userId = funding.user_id || projectUser?.user?._id || projectUser?.user;
                            const userEmail = funding.user_email || projectUser?.email;
                            handleAddAccountToDistribution(funding.account_number, funding.source_of_funds, userId, userEmail);
                          }}
                          disabled={accountDistributions.some(dist => {
                            const distUserMatch = funding.user_email ? dist.user_email === funding.user_email 
                              : (funding.user_id && dist.user_id && dist.user_id.toString() === funding.user_id.toString());
                            return dist.account_number === funding.account_number && distUserMatch;
                          })}
                        >
                          {displayAccountNumber(funding.account_number)}
                        </Button>
                      ))}
                    </Box>
                    <TableContainer component={Paper} sx={{ margin: '16px 0', border: '1px solid #e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
                      <Table size="small" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Account Number</TableCell>
                            <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Source of Funds</TableCell>
                            <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Percentage Share (%)</TableCell>
                            <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Amount Share ($)</TableCell>
                            <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, padding: '12px' }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(accountDistributions || []).map((dist, index) => (
                            <TableRow key={`account-dist-${dist.user_id || dist.user_email}-${dist.account_number}-${index}`}>
                              <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>{displayAccountNumber(dist.account_number)}</TableCell>
                              <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>{dist.source_of_funds}</TableCell>
                              <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>
                                <TextField
                                  type="text"
                                  value={dist.percent_share}
                                  onChange={(e) => handleAccountDistributionChange(index, e.target.value)}
                                  onBlur={(e) => {
                                    if (e.target.value === '') {
                                      handleAccountDistributionChange(index, '0');
                                    }
                                  }}
                                  inputProps={{ 
                                    inputMode: 'decimal',
                                    pattern: '[0-9]*\\.?[0-9]*',
                                    style: { textAlign: 'right' }
                                  }}
                                  size="small"
                                  sx={{ width: '100px' }}
                                />
                              </TableCell>
                              <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>
                                ${(((calculateTotalBillAmount() || 0) * (dist.percent_share || 0)) / 100).toFixed(2)}
                              </TableCell>
                              <TableCell sx={{ padding: '12px', borderBottom: '1px solid #e0e0e0' }}>
                                <Tooltip title="Remove from distribution only">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleRemoveAccountFromDistribution(index)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove from project (and distribution)">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleRemoveAccountFromProjectAndDistribution(index)}
                                    color="warning"
                                  >
                                    <RemoveCircleOutlineIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                          {/* Add Account Row */}
                          <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                            <TableCell colSpan={5} sx={{ padding: '16px', borderBottom: '1px solid #e0e0e0' }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                  Add Account to Project
                                </Typography>
                                
                                {/* User Selection */}
                                <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                  <Typography variant="body2" sx={{ minWidth: '80px' }}>User:</Typography>
                                  <select
                                    value={selectedUserForNewAccount}
                                    onChange={(e) => handleUserSelectionForNewAccount(e.target.value)}
                                    style={{
                                      flex: 1,
                                      padding: '8px',
                                      fontSize: '14px',
                                      border: '1px solid #ccc',
                                      borderRadius: '4px',
                                      backgroundColor: 'white'
                                    }}
                                  >
                                    <option value="">Select a user from the project</option>
                                    {(selectedProject?.users || []).map((user, index) => (
                                      <option key={index} value={user.email}>
                                        {user.first_name} {user.last_name} ({user.email})
                                      </option>
                                    ))}
                                  </select>
                                </Box>

                                {/* Account Selection */}
                                {selectedUserForNewAccount && (
                                  <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ minWidth: '80px' }}>Account:</Typography>
                                    {loadingUserAccounts ? (
                                      <Typography>Loading...</Typography>
                                    ) : (
                                      <select
                                        value={selectedAccountId}
                                        onChange={(e) => handleAccountSelection(e.target.value)}
                                        style={{
                                          flex: 1,
                                          padding: '8px',
                                          fontSize: '14px',
                                          border: '1px solid #ccc',
                                          borderRadius: '4px',
                                          backgroundColor: 'white'
                                        }}
                                      >
                                        <option value="">Select an account</option>
                                        {selectedUserAccounts.map((account) => (
                                          <option key={account._id} value={account._id}>
                                            {account.number} - {account.source}
                                          </option>
                                        ))}
                                        <option value="new">+ Add New Account</option>
                                      </select>
                                    )}
                                  </Box>
                                )}

                                {/* Account Details */}
                                {selectedUserForNewAccount && (isNewAccount || selectedAccountId === 'new' || selectedAccountId) && (
                                  <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ minWidth: '80px' }}>Details:</Typography>
                                    <input
                                      type="text"
                                      placeholder="Account number"
                                      value={newAccountNumber}
                                      onChange={(e) => setNewAccountNumber(e.target.value)}
                                      disabled={!isNewAccount && selectedAccountId !== 'new'}
                                      style={{
                                        flex: 1,
                                        padding: '8px',
                                        fontSize: '14px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        backgroundColor: (!isNewAccount && selectedAccountId !== 'new') ? '#f5f5f5' : 'white'
                                      }}
                                    />
                                    <input
                                      type="text"
                                      placeholder="Source of funds"
                                      value={newSourceOfFunds}
                                      onChange={(e) => setNewSourceOfFunds(e.target.value)}
                                      disabled={!isNewAccount && selectedAccountId !== 'new'}
                                      style={{
                                        flex: 1,
                                        padding: '8px',
                                        fontSize: '14px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        backgroundColor: (!isNewAccount && selectedAccountId !== 'new') ? '#f5f5f5' : 'white'
                                      }}
                                    />
                                    <Button 
                                      onClick={handleAddAccountToProject}
                                      variant="contained"
                                      size="small"
                                      disabled={!selectedUserForNewAccount || !newAccountNumber || !newSourceOfFunds}
                                    >
                                      {isNewAccount || selectedAccountId === 'new' ? 'Add New' : 'Add'}
                                    </Button>
                                  </Box>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={2} align="right" sx={{ fontWeight: 'bold', padding: '12px', borderBottom: 'none' }}>
                              <strong>Total:</strong>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', padding: '12px', borderBottom: 'none' }}>
                              <strong>
                                {((accountDistributions || []).reduce((sum, dist) => {
                                  const value = dist.percent_share === '' ? 0 : Number(dist.percent_share || 0);
                                  return sum + value;
                                }, 0)).toFixed(2)}%
                              </strong>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', padding: '12px', borderBottom: 'none' }}>
                              <strong>${(calculateTotalBillAmount() || 0).toFixed(2)}</strong>
                            </TableCell>
                            <TableCell sx={{ padding: '12px', borderBottom: 'none' }}></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {accountDistributions.reduce((sum, dist) => {
                      const value = dist.percent_share === '' ? 0 : Number(dist.percent_share);
                      return sum + value;
                    }, 0) !== 100 && (
                      <Typography sx={{ color: '#d32f2f', marginTop: '8px' }}>
                        Total percentage must equal 100%
                      </Typography>
                    )}
                  </>
                )}
            </Paper>
            )}

            {/* Horizontal Divider */}
            <Box sx={{ height: '1px', background: 'linear-gradient(to right, var(--brand-primary, #c41e3a), #e0e0e0, transparent)', my: 3 }} />

            {/* Account Mapping Section */}
            <Paper elevation={0} sx={{ 
              background: 'white',
              border: '1.5px solid #dee2e6',
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <Typography variant="h6" sx={{ 
                color: 'var(--brand-primary-text, #c41e3a)', 
                fontWeight: 600, 
                marginBottom: '20px',
                paddingLeft: '12px',
                borderLeft: '4px solid var(--brand-primary, #c41e3a)'
              }}>
                Account Mapping (Accounts Receivable) *
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The facility account that will receive this revenue. Required &mdash; it is sent to the
                business office as the &ldquo;Project-Phase to receive the revenue&rdquo;.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" required error={!accountMapping.facility_id}>
                    <InputLabel>Facility</InputLabel>
                    <Select
                      value={accountMapping.facility_id}
                      onChange={(e) => handleFacilityChange(e.target.value)}
                      label="Facility"
                    >
                      {facilities.map(facility => (
                        <MenuItem key={facility._id} value={facility._id}>
                          {facility.facility_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    size="small"
                    required
                    disabled={!accountMapping.facility_id}
                    error={!!accountMapping.facility_id && !accountMapping.account_number}
                  >
                    <InputLabel>Account</InputLabel>
                    <Select
                      value={accountMapping.account_number}
                      onChange={(e) => handleAccountMappingChange(e.target.value)}
                      label="Account"
                    >
                      {selectedFacilityAccounts.map((account, index) => (
                        <MenuItem key={index} value={account.account_number || ''}>
                          {account.account_number || 'N/A'} - {account.description || 'No description'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ 
        padding: '20px 32px', 
        borderTop: '2px solid #dee2e6', 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        gap: '12px'
      }}>
        {displayMode === 'summary' ? (
          <>
            <Button 
              onClick={onClose}
              sx={{
                color: '#495057',
                fontWeight: 500,
                padding: '10px 24px',
                border: '1.5px solid #adb5bd',
                borderRadius: '6px',
                textTransform: 'none',
                '&:hover': {
                  background: '#f8f9fa',
                  borderColor: '#6c757d',
                }
              }}
            >
              Close
            </Button>
            <Button 
              onClick={handleCreateNewInvoice} 
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, var(--brand-primary, #c41e3a) 0%, var(--brand-primary-hover, #a01830) 100%)',
                fontWeight: 600,
                padding: '10px 24px',
                borderRadius: '6px',
                textTransform: 'none',
                boxShadow: '0 2px 4px var(--brand-focus-ring, rgba(196, 30, 58, 0.4))',
                '&:hover': {
                  background: 'linear-gradient(135deg, var(--brand-primary-hover, #a01830) 0%, var(--brand-primary-active, #8a1529) 100%)',
                  boxShadow: '0 4px 8px var(--brand-focus-ring, rgba(196, 30, 58, 0.4))',
                }
              }}
            >
              Create Another Invoice
            </Button>
          </>
        ) : (
          <>
            <Button 
              onClick={onClose}
              sx={{
                color: '#495057',
                fontWeight: 500,
                padding: '10px 24px',
                border: '1.5px solid #adb5bd',
                borderRadius: '6px',
                textTransform: 'none',
                '&:hover': {
                  background: '#f8f9fa',
                  borderColor: '#6c757d',
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateInvoice} 
              variant="contained"
              disabled={loading}
              sx={{
                background: loading ? '#adb5bd' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                fontWeight: 600,
                padding: '10px 28px',
                borderRadius: '6px',
                textTransform: 'none',
                boxShadow: loading ? 'none' : '0 2px 4px rgba(16, 185, 129, 0.3)',
                '&:hover': {
                  background: loading ? '#adb5bd' : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  boxShadow: loading ? 'none' : '0 4px 8px rgba(16, 185, 129, 0.4)',
                },
                '&.Mui-disabled': {
                  color: 'white',
                  opacity: 0.7,
                }
              }}
            >
              {loading ? 'Generating...' : 'Generate Invoice'}
            </Button>
          </>
        )}
      </DialogActions>

    </Dialog>
  );
};

export default GenerateNewInvoice; 